import React from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
// Mock data for campaign conversion funnel
const conversionData = [{
  name: 'Views',
  value: 5200,
  color: '#6E9FD9'
}, {
  name: 'Clicks',
  value: 3800,
  color: '#D9984B'
}, {
  name: 'Applied',
  value: 2100,
  color: '#9C7CB6'
}, {
  name: 'Completed',
  value: 1450,
  color: '#7FC29B'
}];
// Mock data for campaign code usage over time (last 30 days)
const generateCodeUsageData = () => {
  return Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    // Create a pattern with some variability
    let usageCount = 30 + Math.floor(Math.random() * 40);
    // Add a peak in the middle
    if (i > 10 && i < 20) {
      usageCount += 20 + Math.floor(Math.random() * 30);
    }
    return {
      date: date.toISOString().split('T')[0],
      count: usageCount
    };
  });
};
// Mock data for payment volume over time (last 30 days)
const generatePaymentVolumeData = () => {
  return Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    // Create a pattern with some variability
    let amount = 1500 + Math.floor(Math.random() * 1000);
    // Add a peak in the middle
    if (i > 10 && i < 20) {
      amount += 1000 + Math.floor(Math.random() * 1500);
    }
    return {
      date: date.toISOString().split('T')[0],
      amount: amount
    };
  });
};
// Format date for display
const formatDate = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};
// Conversion Funnel Chart
export const ConversionFunnelChart = () => {
  const funnelSummary = conversionData.map(d => `${d.name} ${d.value.toLocaleString()}`).join(', ');
  return <div className="h-80" role="img" aria-label={`Conversion funnel: ${funnelSummary}`} data-id="element-11403" data-mp="r05nz4vg2bo5">
      <ResponsiveContainer width="100%" height="100%" data-id="element-11404" data-mp="vz8rgkvs09u0">
        <BarChart data={conversionData} layout="vertical" margin={{
        top: 20,
        right: 30,
        left: 40,
        bottom: 5
      }} data-id="element-11405" data-mp="101n7kx828vhl">
          <CartesianGrid strokeDasharray="3 3" data-id="element-11406" data-mp="8c0my56694t1" />
          <XAxis type="number" data-id="element-11407" data-mp="cdcg2ko9885z" />
          <YAxis dataKey="name" type="category" data-id="element-11408" data-mp="cc1b4hj2ouda" />
          <Tooltip formatter={value => [`${value.toLocaleString()}`, 'Count']} data-id="element-11409" data-mp="cb9dz29tuokw" />

          <Legend data-id="element-11410" data-mp="3gralj3aurrw2" />
          <Bar dataKey="value" name="Users" data-id="element-11411" data-mp="3gru3gk0i6ga3">
            {conversionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-11412" data-mp="3ksdyni1in9tl" data-mpi={index} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>;
};
// Conversion Rate Pie Chart
export const ConversionRatePieChart = () => {
  const {
    t
  } = useTranslation('shops');
  // Calculate conversion rates
  const viewToClick = (conversionData[1].value / conversionData[0].value * 100).toFixed(1);
  const clickToApply = (conversionData[2].value / conversionData[1].value * 100).toFixed(1);
  const applyToComplete = (conversionData[3].value / conversionData[2].value * 100).toFixed(1);
  const overallConversion = (conversionData[3].value / conversionData[0].value * 100).toFixed(1);
  const rateData = [{
    name: 'View → Click',
    value: parseFloat(viewToClick),
    color: '#6E9FD9'
  }, {
    name: 'Click → Apply',
    value: parseFloat(clickToApply),
    color: '#D9984B'
  }, {
    name: 'Apply → Complete',
    value: parseFloat(applyToComplete),
    color: '#9C7CB6'
  }];
  const rateSummary = rateData.map(d => `${d.name} ${d.value}%`).join(', ');
  return <div className="h-80" role="img" aria-label={`Conversion rates: ${rateSummary}. Overall conversion ${overallConversion}%.`} data-id="element-11413" data-mp="ajwb7ghpp1w2">
      <ResponsiveContainer width="100%" height="100%" data-id="element-11414" data-mp="qyrx84wb8we">
        <PieChart data-id="element-11415" data-mp="q5pgjcyz5k2">
          <Pie data={rateData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
          name,
          percent
        }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-11416" data-mp="5qjqhhg5lf8l">
            {rateData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-11417" data-mp="3e0v1477u3kig" data-mpi={index} />)}
          </Pie>
          <Tooltip formatter={value => [`${value}%`, 'Conversion Rate']} data-id="element-11418" data-mp="3izpl3chjff45" />
          <Legend data-id="element-11419" data-mp="1c7oos0nfiodc" />
        </PieChart>
      </ResponsiveContainer>
      <div className="text-center mt-2 text-sm font-medium text-gray-700" data-id="element-11420" data-mp="1h6i5e1y0ivhq">
        {t('components.ConversionRatePieChart.overall_conversion')}{' '}
        {overallConversion}%
      </div>
    </div>;
};
// Code Usage Time Series Chart
export const CodeUsageTimeSeriesChart = () => {
  const codeUsageData = generateCodeUsageData();
  const total = codeUsageData.reduce((s, d) => s + d.count, 0);
  return <div className="h-80" role="img" aria-label={`Promo code usage over the last 30 days. Total uses: ${total.toLocaleString()}.`} data-id="element-11421" data-mp="36n3uvvalgdww">
      <ResponsiveContainer width="100%" height="100%" data-id="element-11422" data-mp="3kglipsabccsq">
        <AreaChart data={codeUsageData} margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      }} data-id="element-11423" data-mp="3few3e0g25cyu">
          <CartesianGrid strokeDasharray="0" vertical={false} data-id="element-11424" data-mp="3pdlp64b2rhkd" />
          <XAxis dataKey="date" tickFormatter={formatDate} interval={4} data-id="element-11425" data-mp="3pc0p5z5zr6f4" />
          <YAxis axisLine={false} data-id="element-11426" data-mp="1zz71lnbsm0qg" />
          <Tooltip formatter={value => [value.toLocaleString(), 'Usage Count']} labelFormatter={label => formatDate(label)} data-id="element-11427" data-mp="1v03w61rwhshy" />

          <Legend data-id="element-11428" data-mp="2kpsrypvp3jxi" />
          <Area type="monotone" dataKey="count" stroke="#5DAFBA" fill="#5DAFBA" fillOpacity={0.3} name="Code Usage" data-id="element-11429" data-mp="2kqc944diu4ah" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
};
// Payment Volume Time Series Chart
export const PaymentVolumeTimeSeriesChart = ({
  currency = 'MYR'
}) => {
  const paymentVolumeData = generatePaymentVolumeData();
  const total = paymentVolumeData.reduce((s, d) => s + d.amount, 0);
  return <div className="h-80" role="img" aria-label={`Payment volume over the last 30 days. Total: ${currency} ${total.toLocaleString()}.`} data-id="element-11430" data-mp="3cf5oy100hfjj">
      <ResponsiveContainer width="100%" height="100%" data-id="element-11431" data-mp="3hdqrw86nedbo">
        <AreaChart data={paymentVolumeData} margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      }} data-id="element-11432" data-mp="3mdv8dy7k381w">
          <CartesianGrid strokeDasharray="0" vertical={false} data-id="element-11433" data-mp="1fp2wupe089om" />
          <XAxis dataKey="date" tickFormatter={formatDate} interval={4} data-id="element-11434" data-mp="1fqe5b2iea5m2" />
          <YAxis axisLine={false} data-id="element-11435" data-mp="1aoylfdrye559" />
          <Tooltip formatter={value => [`${currency} ${value.toLocaleString('en-US')}`, 'Volume']} labelFormatter={label => formatDate(label)} data-id="element-11436" data-mp="1as2uopk9zr2s" />

          <Legend data-id="element-11437" data-mp="1yfax8zqbkm14" />
          <Area type="monotone" dataKey="amount" stroke="#7FC29B" fill="#7FC29B" fillOpacity={0.3} name="Payment Volume" data-id="element-11438" data-mp="1yfu7du9qa64l" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
};